import { CheckCircleOutline } from "@mui/icons-material";
import AnnouncementIcon from "@mui/icons-material/Announcement";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import DashboardIcon from "@mui/icons-material/Dashboard";
import InfoIcon from "@mui/icons-material/Info";
import MenuIcon from "@mui/icons-material/Menu";
import NewspaperIcon from "@mui/icons-material/Newspaper";
import PersonIcon from "@mui/icons-material/Person";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import QuestionAnswerIcon from "@mui/icons-material/QuestionAnswer";
import SettingsIcon from "@mui/icons-material/Settings";
import { Badge } from "@mui/material";
import AppBar from "@mui/material/AppBar";
import IconButton from "@mui/material/IconButton";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import { makeStyles } from "@mui/styles";
import axios from "axios";
import PropTypes from "prop-types";
import { Fragment, useCallback, useEffect, useState } from "react";
import { osName, osVersion } from "react-device-detect";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import ectoollogo from '../../assets/images/ectool_Logo_2019.png';
import { API_BASE_URL } from "../../constants/apiConstants";
import { DEBUG_FLAG } from "../../constants/debug";
import { LOCAL_STORAGE_KEY } from "../../constants/localStorage";
import { VERSION } from "../../constants/version";
import { useAuth, useClient } from "../../context/auth-context";
import { useConfirm } from "../../context/confirm-context";
import { useMessage } from "../../context/message-context";
import { useZulassung } from "../../context/zulassungs-context";
import { useLocalStorage, useSessionStorage } from "../../utils/hooks";
import { SchwarzesBrettHeader } from "../SchwarzesBrett";
import MessageBox from "../shared/components/MessageBox";
import NavigationDrawer from "../shared/components/NavigationDrawer";
import SupportDialog from "./SupportDialog";

const useStyles = makeStyles((theme) => ({
  appBar: {
    backgroundColor: theme.palette.background.paper,
    backgroundImage: "none",
    borderBottom: "1px solid " + theme.palette.background.light,
    boxShadow: "none",
    position: "fixed",
    top: 0,
    paddingTop: "env(safe-area-inset-top)",
    touchAction: "none",
  },
  title: {
    marginLeft: theme.spacing(2),
    flex: 1,
    color: theme.palette.getContrastText(theme.palette.background.default),
  },
  icon: {
    color: theme.palette.primary,
  },
  debugTitle: {
    marginLeft: theme.spacing(2),
    flex: 1,
    color: theme.palette.error.main,
  },
  verifyTitle: {
    marginLeft: theme.spacing(2),
    flex: 1,
    color: theme.palette.secondary.main,
  },
}));

function Header(props) {
  const { selectedTab, title, onTitleChange } = props;
  const releaseVersion = localStorage.getItem(LOCAL_STORAGE_KEY.version);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const classes = useStyles();
  const { logout, user } = useAuth();
  const apiRequest = useClient();
  const [accessToken] = useSessionStorage(LOCAL_STORAGE_KEY.accessToken);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [fileUrl, setFileUrl] = useState(ectoollogo);
  const confirm = useConfirm();
  const [supportDialog, setSupportDialog] = useState(false);
  const [supportAiDialog, setSupportAiDialog] = useState(false);
  const [offeneAufgaben, setOffeneAufgaben] = useSessionStorage(LOCAL_STORAGE_KEY.offeneAufgaben);
  const { showSuccessMessage, showErrorMessage } = useMessage();
  const [deviceId] = useLocalStorage(DEBUG_FLAG() ? LOCAL_STORAGE_KEY.deviceIdLocal : LOCAL_STORAGE_KEY.deviceId);
  const [username] = useLocalStorage(LOCAL_STORAGE_KEY.username);
  const location = useLocation();
  const [messageBoxOpen, setMessageBoxOpen] = useState(false);
  const { zulassung, updateMandant } = useZulassung();

  useEffect(() => {
    const payload = {
      file: "logo",
    };
    const headers = {
      Authorization: "Bearer " + accessToken,
      Accept: "image/png, image/jpeg",
    };
    axios
      .post(API_BASE_URL + "resources", payload, {
        headers: headers,
        responseType: "blob",
      })
      .then((response) => {
        const url = window.URL.createObjectURL(response.data);
        setFileUrl(url);
      })
      .catch(function (error) {
        console.log(error);
      });
    // eslint-disable-next-line
  }, []);

  const openMessageBox = () => {
    setMessageBoxOpen(true);
  };

  const closeMessageBox = () => {
    setMessageBoxOpen(false);
  };

  function messageBoxResult(r) {
    if (r === "YES") {
      const payload = {
        file: "logo",
      };
      const headers = {
        Authorization: "Bearer " + accessToken,
        Accept: "image/png, image/jpeg",
      };
      axios
        .post(API_BASE_URL + "verify-account", payload, { headers: headers })
        .then((response) => {
          showSuccessMessage(response.data.message);
          closeMobileDrawer();
          logout();
        })
        .catch(function (error) {
          if (error.response) {
            showErrorMessage(error.response.data.message);
          }
          console.log(error);
        });
    } else {
      closeMobileDrawer();
      apiRequest("logout").then(() => logout());
    }
    closeMessageBox();
  }

  const verifyAccount = () => {
    openMessageBox();
  };

  const openMobileDrawer = useCallback(() => {
    setIsMobileOpen(true);
  }, [setIsMobileOpen]);

  const closeMobileDrawer = useCallback(() => {
    setIsMobileOpen(false);
  }, [setIsMobileOpen]);

  const doLogout = () => {
    if (user.pruefer === "1") {
      verifyAccount();
      return;
    }
    closeMobileDrawer();
    apiRequest("logout").then(() => logout());
  };

  const sendSupportRequest = () => {
    const link = document.createElement("a");
    link.href =
      "mailto:support@ectool.de?subject=Supportanfrage&body=%0d%0a%0d%0a%0d%0a------------------------------%0d%0aWichtige Informationen für ectool:%0d%0aBitte hier nichts verändern%0d%0aVersion: " +
      releaseVersion +
      "%0d%0aBuild: " +
      VERSION +
      "%0d%0aDeviceId: " +
      deviceId +
      "%0d%0aBetriebssystem: " +
      osName +
      "%0d%0aUsername: " +
      username +
      "%0d%0aAufruf: Menu%0d%0aPfad: " +
      location.pathname +
      "%0d%0a------------------------------";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    closeMobileDrawer();
  };

  const zeigehilfe = () => {
    const url = "https://docs.ectool.de/app";
    const link = document.createElement("a");
    link.href = url;
    link.target = "_blanc";
    link.ref = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    closeMobileDrawer();
  };

  const menuItems = [
    {
      link: "/",
      name: t("Verteiler"),
      onClick: closeMobileDrawer,
      icon: {
        desktop: <DashboardIcon className={selectedTab === "Dashboard" ? classes.textPrimary : classes.icon} fontSize="small" />,
        mobile: <DashboardIcon className={classes.icon} />,
      },
    },
    {
      link: "/profile",
      name: t("Account"),
      onClick: closeMobileDrawer,
      icon: {
        desktop: <PersonIcon className={selectedTab === "Account" ? classes.textPrimary : classes.icon} fontSize="small" />,
        mobile: <PersonIcon className={classes.icon} />,
      },
    },
    {
      link: "/settings",
      name: t("Einstellungen"),
      onClick: closeMobileDrawer,
      icon: {
        desktop: <SettingsIcon className={selectedTab === "Einstellungen" ? classes.textPrimary : classes.icon} fontSize="small" />,
        mobile: <SettingsIcon className={classes.icon} />,
      },
    },
    {
      name: t("Hilfe"),
      onClick: zeigehilfe,
      icon: {
        desktop: <InfoIcon className={classes.icon} fontSize="small" />,
        mobile: <InfoIcon className={classes.icon} />,
      },
    },
    {
      link: "/NEWS",
      name: t("Programmanpassungen"),
      onClick: closeMobileDrawer,
      icon: {
        desktop: <NewspaperIcon className={classes.icon} fontSize="small" />,
        mobile: <NewspaperIcon className={classes.icon} />,
      },
    },
    {
      link: "/BRETTL",
      name: t("Schwarzes Brett"),
      onClick: closeMobileDrawer,
      icon: {
        desktop: <AnnouncementIcon className={classes.icon} fontSize="small" />,
        mobile: <AnnouncementIcon className={classes.icon} />,
      },
    },
    {
      name: t("Support kontaktieren"),
      onClick: () => setSupportDialog(true),
      icon: {
        desktop: <QuestionAnswerIcon className={selectedTab === "Support kontaktieren" ? classes.textPrimary : classes.icon} fontSize="small" />,
        mobile: <QuestionAnswerIcon className={classes.icon} />,
      },
    },
    {
      name: t("Logout"),
      onClick: doLogout,
      icon: {
        desktop: <PowerSettingsNewIcon className={classes.icon} fontSize="small" />,
        mobile: <PowerSettingsNewIcon className={classes.icon} />,
      },
    },
  ];

  if (user?.id === "0009737") {
    menuItems?.splice(menuItems?.length - 1, 0, {
      name: t("Support kontaktieren Neu (AI)"),
      onClick: () => setSupportAiDialog(true),
      icon: {
        desktop: <QuestionAnswerIcon className={selectedTab === "Support kontaktieren" ? classes.textPrimary : classes.icon} fontSize="small" />,
        mobile: <QuestionAnswerIcon className={classes.icon} />,
      },
    });
  }

  const hatAufgabenZulassung = () => {
    const currentProgram = zulassung.programs.find((obj) => obj.prg === "WEBAGU");
    if (currentProgram?.mandanten?.length > 0) {
      return true;
    }
    return false;
  };

  const changeAufgabenZulassung = () => {
    const currentProgram = zulassung.programs.find((obj) => obj.prg === "WEBAGU");
    if (currentProgram?.mandanten?.length > 0) {
      updateMandant(currentProgram.mandanten[0].key);
    }
  };

  const handleAufgabenClick = () => {
    if (hatAufgabenZulassung()) {
      changeAufgabenZulassung();
      onTitleChange("");
      navigate("/WEBAGU");
    }
  };

  const backButtonClicked = () => {
    if (location.pathname === "/verify-admincode") {
      navigate(-2);
    } else if (location.pathname === "/FORGEN") {
      if (localStorage.getItem("formular") !== "") {
        confirm({
          text: "Sind Sie sicher, dass Sie den Formulargenerator verlassen möchten ? Alle nicht gespeicherten Änderungen gehen dadurch verloren!",
          title: "Hinweis",
          buttons: "YESNO",
        })
          .then(() => {
            navigate(-1);
          })
          .catch(() => {});
      } else {
        navigate(-1);
      }
    } else {
      navigate(-1);
    }
  };

  return (
    <Fragment>
      <AppBar className={classes.appBar}>
        <Toolbar>
          {location.pathname !== "/" &&
            location.pathname !== "/login/device" &&
            location.pathname !== "/login/password" &&
            location.pathname !== "/login/user" && (
              <IconButton edge="start" onClick={() => backButtonClicked()} aria-label="back">
                <ArrowBackIosIcon />
              </IconButton>
            )}
          {!title && fileUrl && <img src={fileUrl} height="30" alt="ectool" loading="lazy" />}
          <Typography variant="h6" className={classes.title}>
            {title || ""}
          </Typography>
          {user.pruefer === "1" && (
            <Typography variant="h6" className={classes.verifyTitle}>
              Prüfer von {user.name}
            </Typography>
          )}
          {user.tmd && (
            <Typography variant="h6" className={classes.verifyTitle}>
              Testumgebung: {user.tmd}
            </Typography>
          )}
          {DEBUG_FLAG() && (
            <Typography variant="h6" className={classes.debugTitle}>
              {t("-LOKAL-")}
            </Typography>
          )}

          {location.pathname !== "/BRETTL" &&
            <SchwarzesBrettHeader></SchwarzesBrettHeader>
          }

          {location.pathname !== "/WEBAGU" && hatAufgabenZulassung() && (
            <IconButton onClick={handleAufgabenClick} aria-label="todos">
              {offeneAufgaben !== "0" ? (
                <Badge badgeContent={offeneAufgaben} color="error">
                  <CheckCircleOutline />
                </Badge>
              ) : (
                <CheckCircleOutline />
              )}
            </IconButton>
          )}
          <IconButton onClick={openMobileDrawer} aria-label="menu">
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <NavigationDrawer
        menuItems={menuItems.map((element) => ({
          link: element.link,
          name: element.name,
          icon: element.icon.mobile,
          onClick: element.onClick,
        }))}
        anchor="right"
        open={isMobileOpen}
        selectedItem={selectedTab}
        onClose={closeMobileDrawer}
      />
      <MessageBox
        title={t("Hinweis")}
        text={t("Wollen Sie den Zugang freigeben?")}
        buttons="YESNO"
        onClose={closeMessageBox}
        open={messageBoxOpen}
        handleResponse={messageBoxResult}
      />
      <SupportDialog supportDialog={supportDialog} setSupportDialog={setSupportDialog}></SupportDialog>
      {/* <SupportAiDialog supportDialog={supportDialog} setSupportDialog={setSupportDialog}></SupportAiDialog> */}
    </Fragment>
  );
}
Header.propTypes = {
  title: PropTypes.string.isRequired,
};
export default Header;
